import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { clikArtService } from '@/api/services/clikart.service'
import { queryKeys } from '@/api/query-keys'

const CLIKART_SEARCH_DEBOUNCE_MS = 300

/**
 * Debounces the free-text `displayName` search (typing is bursty, and unlike Backgrounds/Photos
 * this hits a live search endpoint on every change) but not `keywordIds` — a keyword pick is
 * already a discrete, deliberate action, matching `usePhotoSearchQuery`'s treatment of filename
 * search. Both are optional and safe to omit together (see clikart.service.ts).
 */
export function useClikArtSearchQuery(displayName: string, keywordIds: number[]) {
  const trimmedName = displayName.trim()
  const [debouncedName, setDebouncedName] = useState(trimmedName)

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedName(trimmedName)
    }, CLIKART_SEARCH_DEBOUNCE_MS)

    return () => window.clearTimeout(timeoutId)
  }, [trimmedName])

  // Sorted so the query key (and upstream request) is stable regardless of selection order.
  const sortedKeywordIds = useMemo(() => [...keywordIds].sort((a, b) => a - b), [keywordIds])

  return useQuery({
    queryKey: queryKeys.clikArt.search({
      displayName: debouncedName,
      keywordIds: sortedKeywordIds,
    }),
    queryFn: () =>
      clikArtService.searchClikArt({ displayName: debouncedName, keywordIds: sortedKeywordIds }),
    staleTime: 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
  })
}
